package dev.kuml.portal.web.editor

/**
 * The diagram's current node/edge selection (R-27, kUML Portal "Interactive Diagram-Editing UI"
 * wave) -- a pure value + pure transitions, so `DiagramCanvas.kt`'s click/keyboard handling only
 * ever needs to call [DiagramSelection]'s functions and re-render from the result, never mutate
 * selection state ad hoc inline.
 *
 * A node click and an edge click are mutually exclusive by construction: selecting an edge always
 * clears [nodeIds] and vice versa (there is no "a node and an edge selected together" state) --
 * matches the design-team ruling that a relationship is inspected on its own, never alongside a
 * node.
 *
 * @property nodeIds Selected node (classifier) ids, in click order. `0` for none selected, `1` for a
 *   single classifier's inspector, `2` for the "create association" affordance (see
 *   `InspectorModel.kt`) -- MORE than 2 is possible via shift-click (see [shiftClick]) but the
 *   inspector panel only ever offers an action for exactly 2.
 * @property edgeId The selected relationship's id, or `null` if a node (or nothing) is selected.
 */
public data class SelectionState(
    val nodeIds: List<String> = emptyList(),
    val edgeId: String? = null,
)

public object DiagramSelection {
    /** A plain (non-shift) click on node [id] -- replaces the whole selection with just this node. */
    public fun click(
        current: SelectionState,
        id: String,
    ): SelectionState = SelectionState(nodeIds = listOf(id))

    /**
     * A shift-click on node [id] -- toggles [id] in/out of the current node selection, preserving
     * click order for everything else (so a third shift-click re-adds a just-removed node at the
     * END of the list, not its original position -- matches ordinary multi-select conventions).
     * Clears any edge selection: shift-clicking a node while an edge was selected starts a fresh
     * node-only selection with just [id], mirroring [click]'s "nodes and edges never mix" rule.
     */
    public fun shiftClick(
        current: SelectionState,
        id: String,
    ): SelectionState {
        if (current.edgeId != null) return SelectionState(nodeIds = listOf(id))
        val nodeIds = if (id in current.nodeIds) current.nodeIds - id else current.nodeIds + id
        return SelectionState(nodeIds = nodeIds)
    }

    /** Selects relationship [id] -- clears any node selection (see this class's KDoc). Selecting the
     * SAME edge again is a no-op (idempotent), matching [click]'s idempotence for a re-click of an
     * already-sole-selected node. */
    public fun selectEdge(
        current: SelectionState,
        id: String,
    ): SelectionState = SelectionState(edgeId = id)

    /** Clears the selection entirely -- Escape key, or a click on empty canvas. */
    public fun escape(): SelectionState = SelectionState()

    /**
     * Filters [state] against [knownIds] -- called after every diagram refresh (a new
     * `/render`+`/outline` pair landing from a [dev.kuml.portal.server.collab] `FRAME_KIND_MODEL_REV`,
     * see `EditorScreen.kt`'s `refreshDiagramFromServer`) so a selection surviving a re-render never
     * references an id that just stopped existing (e.g. this connection's own delete, or a
     * collaborator's concurrent edit). Node ids not in [knownIds] are dropped individually (a
     * multi-select survives partially); an edge id not in [knownIds] clears the whole edge selection
     * (there is no "partial" edge selection). Never THROWS for an unknown id -- silently drops it,
     * since a stale id here is an expected, ordinary race (see this file's own "nodes/edges never
     * mix" invariant, which this function preserves: it never fabricates a mixed state).
     */
    public fun restore(
        state: SelectionState,
        knownIds: Set<String>,
    ): SelectionState =
        if (state.edgeId != null) {
            if (state.edgeId in knownIds) state else SelectionState()
        } else {
            SelectionState(nodeIds = state.nodeIds.filter { it in knownIds })
        }
}
